import { createFileRoute, useNavigate, useLocation, Link } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { useSelectedSubscription } from '@/lib/use-selected-subscription';
import { api, ApiError, isProviderError, newIntentNonce } from '@/lib/api';
import type { Subscription } from '@/api/types';
import { BottomSheet, ConfirmDialog } from '@/components/ui/dialog';
import {
  Smartphone, Laptop, Snowflake, Gift, Plus, Loader2, Copy, Zap,
  ArrowUp, Wallet, Globe2, ChevronRight, RefreshCw, PlusSquare, CalendarClock, Clock, Send,
  ListChecks, Link2,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { confirmAction, getWebApp, haptic, openInvoiceUrl } from '@/lib/telegram';
import { recurrentPeriodText } from '@/components/recurrent-toggle';
import { useTranslation } from 'react-i18next';
import { Switch } from '@/components/ui/switch';
import {
  Skeleton,
  SkeletonCard,
  SkeletonGrid,
  SkeletonLine,
  SkeletonPage,
} from '@/components/ui/skeleton';
import { IconChip, StatusPill } from '@/components/ui/calm';
import { ProviderPills } from '@/components/ui/provider-pills';
import { cn } from '@/lib/utils';
import { daysLeft as calcDaysLeft, trafficPercent } from '@/lib/subscription-progress';
import { toast } from 'sonner';
import { shortDate } from '@/lib/date';
import { formatMinor, formatWholeMinor, groupDigits, rub } from '@/lib/currency';
import { WITHDRAW_QUERY_KEY } from '@/lib/withdraw';
import { Btn, Group, Row, ScreenTitle, SegmentBar, SubCard, SubSwitcher } from '@/components/ui/tiles';
import { SubscriptionCard } from '@/components/SubscriptionCard';
import { copyToClipboard } from '@/lib/clipboard';

// Сигнал «проскроллить к блоку рефералки» приходит из профиля через router-state
// (а НЕ через URL-hash — hash занят телеграм-параметрами bot_id/initData, см. lib/api.ts,
// перезаписывать его нельзя). HistoryState пустой и расширяемый — аугментируем.
declare module '@tanstack/history' {
  interface HistoryState {
    scrollTo?: 'referral';
    /** Профиль просит сразу открыть шит заморозки (ссылка «Заморозить подписку»). */
    openSheet?: 'freeze';
  }
}

export const Route = createFileRoute('/subscription')({
  component: SubscriptionScreen,
});

/**
 * Пробную подписку заморозить нельзя: AdaptGroup на freeze триала отвечает успехом,
 * но дни продолжают списываться. Раньше клиент видел «Заморожена» и терял пробный
 * период. Бэк теперь отвечает 409 `app.subscriptions.trial_cannot_freeze`, а UI не
 * даёт даже открыть шит.
 */
const TRIAL_FREEZE_NOTE_KEY = 'Trial freeze note';

/** Гигабайты одной точностью: десятые только у малых чисел, «125», а не «125.0». */
const gbText = (n: number) => n.toLocaleString(undefined, { maximumFractionDigits: n < 10 ? 1 : 0 });

/**
 * Вкладка «Подписка»: всё, чем подпиской управляют. Ссылка для приложения, продление,
 * автопродление, заморозка, трафик, устройства, страны. Главная (`/me`) только
 * показывает состояние плитками и ведёт сюда.
 */
function SubscriptionScreen() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const qc = useQueryClient();

  // Все активные подписки в текущем боте (юзер мог купить 2+). Выбранная общая
  // для всех экранов, см. lib/use-selected-subscription.ts.
  // refetchOnMount:'always' — persister в sessionStorage может вернуть stale-данные
  // (например после freeze/unfreeze в прошлой сессии), нам нужен свежий статус с сервера.
  const { subs, sub, select, isPending: subPending } = useSelectedSubscription({
    refetchOnMount: 'always',
  });
  // Истёкшие за 30 дней: без живой подписки показываем карточку «Продлить». Раньше
  // истёкшая пропадала, и человек мог только купить новую, с новой ссылкой.
  const { data: expiredSubs } = useQuery({
    queryKey: ['subs-expired'],
    queryFn: () => api.expiredSubscriptions(),
    enabled: !subPending && !sub,
    staleTime: 60_000,
  });

  // У «Навсегда» превью устройств на главной не грузим: список стоит два запроса к
  // поставщику, а лимит у него общий с покупками всей платформы. Список на /devices.
  const { data: devices } = useQuery({
    queryKey: ['devices', sub?.id],
    queryFn: () => (sub ? api.devices(sub.id) : Promise.resolve([])),
    enabled: !!sub && sub.supplier !== 'scvpn',
    placeholderData: keepPreviousData,
  });

  // Кнопки партнёра для раздела «Мои подписки» (идея #10). Блок необязательный,
  // до ответа его просто нет: ложного пустого состояния тут не бывает.
  const { data: info } = useQuery({
    queryKey: ['client-info'],
    queryFn: () => api.info(),
    placeholderData: keepPreviousData,
  });
  const customSubButtons = info?.custom_buttons?.subs ?? [];
  const [freezeOpen, setFreezeOpen] = useState(false);
  const [trafficOpen, setTrafficOpen] = useState(false);
  // Докупка к «Навсегда»: какой лист открыт (трафик обхода или +15 устройств).
  const [addonKind, setAddonKind] = useState<'traffic' | 'devices' | null>(null);
  const autoRenew = sub?.autoRenew ?? false;

  useEffect(() => {
    const tg = getWebApp();
    tg?.BackButton.hide();
    tg?.MainButton.hide();
  }, []);

  // Выбранная подписка помнится между заходами, и её плитка может стоять третьей:
  // на узком экране лента открывалась с первыми двумя, а выбранной не было видно.
  // Прокручиваем саму ленту, не страницу.
  const pickerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const strip = pickerRef.current?.firstElementChild as HTMLElement | null | undefined;
    const chip = strip?.querySelector<HTMLElement>('[aria-selected="true"]');
    if (!strip || !chip) return;
    const s = strip.getBoundingClientRect();
    const c = chip.getBoundingClientRect();
    if (c.left < s.left || c.right > s.right) strip.scrollLeft += c.left - s.left - 16;
  }, [sub?.id, subs.length]);

  // Из профиля по ссылке «Заморозить подписку» — открываем шит сразу, а не бросаем
  // человека на главную гадать, куда нажимать. Без подписки замораживать нечего.
  // У триала шит не открываем вовсе (см. TRIAL_FREEZE_NOTE_KEY), объясняем тостом.
  useEffect(() => {
    // У «Навсегда» паузы нет: поставщик отбил бы её 409, а клиент увидел бы непонятный тост.
    if (location.state.openSheet !== 'freeze' || !sub || sub.supplier === 'scvpn') return;
    if (sub.isTrial && sub.status !== 'frozen') {
      toast.info(t(TRIAL_FREEZE_NOTE_KEY), {
        duration: 6000,
        action: { label: t('Upgrade action'), onClick: () => navigate({ to: '/upgrade' }) },
      });
      return;
    }
    setFreezeOpen(true);
  }, [location.state.openSheet, sub, navigate]);

  const { mutate: toggleRenewMutation } = useMutation({
    mutationFn: ({ id, enabled, days }: { id: string; enabled: boolean; days: number | null }) =>
      api.toggleAutoRenew(id, enabled, days),
    // Тумблер на главной читает autoRenew из ['subs'], поэтому оптимистично правим
    // именно этот кэш — иначе свитч не реагирует на тап до refetch'а.
    onMutate: async ({ id, enabled, days }) => {
      await qc.cancelQueries({ queryKey: ['subs'] });
      const prevAll = qc.getQueryData<Subscription[]>(['subs']);
      qc.setQueryData<Subscription[]>(['subs'], (list) =>
        list
          ? list.map((s) =>
              s.id === id ? { ...s, autoRenew: enabled, autoRenewDays: days } : s,
            )
          : list,
      );
      return { prevAll };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prevAll !== undefined) qc.setQueryData(['subs'], ctx.prevAll);
      haptic('error');
      toast.error(t('Save failed'));
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['subs'] });
    },
  });

  const autoRenewDays = sub?.autoRenewDays ?? null;

  const toggleAutoRenew = (val: boolean) => {
    toast.success(val ? t('Auto-renew on') : t('Auto-renew off'));
    if (sub) toggleRenewMutation({ id: sub.id, enabled: val, days: autoRenewDays });
  };

  // Автосписание у кассы (карта или СБП). Отключение переспрашивает: кнопка стоит на
  // виду, и промах пальцем не должен молча отключать продление.
  const recurrent = sub?.recurrent ?? null;
  const { mutate: cancelRecurrent, isPending: cancellingRecurrent } = useMutation({
    mutationFn: (id: string) => api.cancelRecurrent(id),
    onSuccess: async () => {
      haptic('success');
      toast.success(t('Auto-payment off toast'));
      await qc.invalidateQueries({ queryKey: ['subs'] });
    },
    onError: () => {
      haptic('error');
      toast.error(t('Auto-payment cancel failed'));
    },
  });
  const askCancelRecurrent = async () => {
    // Замок от двойного нажатия, пока летит запрос (interactivity-21).
    if (!sub || cancellingRecurrent) return;
    const ok = await confirmAction(
      t('Auto-payment cancel confirm', { date: shortDate(sub.expiresAt) }),
      t('Auto-payment cancel title'),
      { okText: t('Turn off'), destructive: true },
    );
    if (ok) cancelRecurrent(sub.id);
  };
  const recurrentHint = recurrent
    ? recurrent.status === 'failing'
      ? t('Auto-payment failing')
      : recurrent.nextPaymentDate
        ? t('Auto-payment hint', {
            amount: formatMinor(recurrent.amountMinor, recurrent.currency),
            period: recurrentPeriodText(t, recurrent.period),
            date: shortDate(recurrent.nextPaymentDate),
          })
        : t('Auto-payment hint nodate', {
            amount: formatMinor(recurrent.amountMinor, recurrent.currency),
            period: recurrentPeriodText(t, recurrent.period),
          })
    : null;

  /** Срок автопродления: период тарифа или свой. Меняется, не трогая тумблер. */
  const setAutoRenewDays = (days: number | null) => {
    if (sub) toggleRenewMutation({ id: sub.id, enabled: sub.autoRenew, days });
  };

  const { mutate: freeze, isPending: freezing } = useMutation({
    mutationFn: async (): Promise<{ wasFrozen: boolean }> => {
      if (!sub) throw new Error('no sub');
      const subId = sub.id;
      // Спрашиваем у сервера актуальный статус — не доверяем локальному cache
      // (sessionStorage persister и keepPreviousData могут отдавать stale-данные).
      const fresh = await api.allSubscriptions();
      const current = fresh.find((s) => s.id === subId);
      const wasFrozen = current ? current.status === 'frozen' : sub.status === 'frozen';
      if (wasFrozen) await api.unfreezeSubscription(subId);
      else await api.freezeSubscription(subId);
      return { wasFrozen };
    },
    onMutate: async () => {
      if (!sub) return { prevAll: undefined };
      const subId = sub.id;
      await qc.cancelQueries({ queryKey: ['subs'] });
      const prevAll = qc.getQueryData<Subscription[]>(['subs']);
      const flip = (s: Subscription): Subscription => ({
        ...s,
        status: s.status === 'frozen' ? 'active' : 'frozen',
      });
      qc.setQueryData<Subscription[]>(['subs'], (list) =>
        list ? list.map((s) => (s.id === subId ? flip(s) : s)) : list,
      );
      // Закрываем диалог сразу — UI уже показывает новое состояние оптимистично.
      setFreezeOpen(false);
      return { prevAll };
    },
    onSuccess: ({ wasFrozen }) => {
      haptic('success');
      toast.success(wasFrozen ? t('Sub unfrozen') : t('Sub frozen'));
    },
    onError: (err, _vars, ctx) => {
      if (ctx?.prevAll !== undefined) qc.setQueryData(['subs'], ctx.prevAll);
      haptic('error');
      // Триал мог проскочить локальный guard (stale-кеш, старая вкладка) — бэк отдаёт
      // 409 с этим кодом, показываем тот же понятный текст, а не «не получилось».
      const errBody = (err instanceof ApiError ? err.body : null) as
        | { error?: { code?: string } }
        | null;
      if (errBody?.error?.code === 'app.subscriptions.trial_cannot_freeze') {
        toast.info(t(TRIAL_FREEZE_NOTE_KEY), { duration: 6000 });
        qc.refetchQueries({ queryKey: ['subs'] });
        return;
      }
      toast.error(t('Refreshing status'));
      // На случай рассинхрона: после fail-а сразу подтягиваем свежий статус с сервера.
      qc.refetchQueries({ queryKey: ['subs'] });
    },
    onSettled: async () => {
      // Force-refetch — invalidate сам по себе ставит query stale, но refetch ждать
      // не будет; при медленной сети UI остаётся в optimistic-состоянии вплоть до
      // следующего mount'а. await refetchQueries синхронно подтягивает свежий статус.
      await qc.refetchQueries({ queryKey: ['subs'] });
    },
  });

  if (subPending && !sub) return <DashboardSkeleton />;
  if (!sub) {
    return (
      <div className="space-y-4 p-4 pb-6">
        <ScreenTitle>{t('Subscription tab')}</ScreenTitle>
        {expiredSubs?.[0] && <ExpiredCard sub={expiredSubs[0]} />}
        <section className="rounded-[var(--r-card)] border border-[var(--t-line)] bg-[var(--t-surface)] p-5 shadow-[var(--t-shadow)]">
          <div className="text-[19px] font-semibold">{t('No subscription yet')}</div>
          <p className="mt-1 text-[14px] leading-snug text-[var(--t-muted)]">{t('No subscription yet hint')}</p>
          <button
            type="button"
            onClick={() => navigate({ to: '/plans' })}
            className="calm-cta mt-4 flex h-12 w-full items-center justify-center rounded-[var(--r-btn)] text-[15px] font-semibold transition-transform active:scale-[0.98]"
          >
            {t('Open plans')}
          </button>
        </section>
      </div>
    );
  }

  // Источник истины — days_left с бэка: он учитывает заморозку (у замороженной
  // подписки end_date стоит на месте, локальный расчёт от Date.now() утекал бы).
  // Локальный calcDaysLeft остаётся фолбэком для mock-данных.
  const daysLeft = sub.daysLeft ?? calcDaysLeft(sub.expiresAt);
  // Последние часы подписки: бэк отдаёт 0 полных дней, и красный «0» читался как
  // «уже закончилась», хотя VPN ещё работает.
  const daysLeftLabel = daysLeft === 0 && new Date(sub.expiresAt).getTime() > Date.now() ? '<1' : String(daysLeft);

  const trafficPct = trafficPercent(sub.trafficUsedGb, sub.trafficTotalGb);
  // Раздел «Навсегда» (FranCenter): сама подписка и её пробник.
  const lifetimeSection = sub.supplier === 'scvpn';
  const isFrozen = sub.status === 'frozen';
  // Триал морозить нельзя; унаследованному «замороженному» триалу разморозку оставляем.
  const freezeBlocked = sub.isTrial && !isFrozen;
  const openFreeze = () => {
    if (freezeBlocked) {
      haptic('error');
      toast.info(t(TRIAL_FREEZE_NOTE_KEY), {
        duration: 6000,
        action: { label: t('Upgrade action'), onClick: () => navigate({ to: '/upgrade' }) },
      });
      return;
    }
    setFreezeOpen(true);
  };

  return (
    <>
      <div className="space-y-4 p-4 pb-6">
        <ScreenTitle>{t('Subscription tab')}</ScreenTitle>

        <div ref={pickerRef}>
          <SubSwitcher items={subs.map((s) => ({ id: s.id, label: s.planName }))} value={sub.id} onChange={select} />
        </div>

        <SubscriptionCard sub={sub} mini />

        {/* Трафик обхода: остаток делениями, как заряд. Подробности под ним одной строкой. */}
        <section className="rounded-[var(--r-card)] border border-[var(--t-line)] bg-[var(--t-surface)] p-4 shadow-[var(--t-shadow)]">
          <div className="flex items-baseline justify-between gap-3 text-[14px] text-[var(--t-muted)]">
            <span>{t('Traffic bypass left')}</span>
            {lifetimeSection && <span className="text-[13px]">{t('Does not burn')}</span>}
          </div>
          <div className="mt-1.5 text-[34px] font-semibold leading-tight tracking-[-0.035em] tabular">
            {sub.trafficTotalGb === null ? '∞' : gbText(Math.max(0, sub.trafficTotalGb - sub.trafficUsedGb))}
            {sub.trafficTotalGb !== null && (
              <span className="ml-1.5 text-[16px] font-medium tracking-normal text-[var(--t-muted)]">
                {t('Traffic of total', { total: gbText(sub.trafficTotalGb) })}
              </span>
            )}
          </div>
          <div className="my-3.5">
            <SegmentBar value={sub.trafficTotalGb ? 1 - trafficPct / 100 : 1} />
          </div>
          <p className="text-[13px] leading-snug text-[var(--t-muted)]">
            {lifetimeSection
              ? t('Traffic note lifetime')
              : sub.isTrial
                ? t('Traffic note trial')
                : t('Traffic note paid', { used: gbText(sub.trafficUsedGb) })}
          </p>
        </section>

        {/* Ссылка для приложения строкой с кнопкой копирования, под ней главное действие. */}
        {sub.configUrl && (
          <div className="flex items-center gap-3 rounded-[var(--r-card)] border border-[var(--t-line)] bg-[var(--t-surface)] py-1.5 pl-4 pr-1.5 shadow-[var(--t-shadow)]">
            <div className="min-w-0 flex-1">
              <div className="text-[12px] text-[var(--t-muted)]">{t('Link for the app')}</div>
              <div className="mt-0.5 truncate font-mono text-[14px] font-medium">{sub.configUrl.replace(/^https?:\/\//, '')}</div>
            </div>
            <button
              type="button"
              aria-label={t('Copy')}
              onClick={async () => {
                const ok = await copyToClipboard(sub.configUrl);
                if (ok) {
                  haptic('success');
                  toast.success(t('Link copied'));
                } else {
                  haptic('error');
                }
              }}
              className="grid size-11 shrink-0 place-items-center rounded-[var(--r-sm)] bg-[var(--t-surface-2)] transition-transform active:scale-[0.95]"
            >
              <Copy className="size-[18px]" />
            </button>
          </div>
        )}

        <div className={cn('grid gap-2', !lifetimeSection || sub.isTrial ? 'grid-cols-2' : 'grid-cols-1')}>
          {sub.configUrl && (
            <Btn icon={<Zap fill="currentColor" />} onClick={() => navigate({ to: '/success/$subscriptionId', params: { subscriptionId: sub.id } })}>
              {t('Connect short')}
            </Btn>
          )}
          {/* У «Навсегда» продлевать нечего. */}
          {!lifetimeSection && (
            <Btn
              variant="ghost"
              icon={<RefreshCw />}
              onClick={() =>
                sub.isTrial ? navigate({ to: '/plans' }) : navigate({ to: '/renew/$subscriptionId', params: { subscriptionId: sub.id } })
              }
            >
              {sub.isTrial ? t('Choose plan') : t('Renew')}
            </Btn>
          )}
          {lifetimeSection && sub.isTrial && (
            <Btn variant="ghost" icon={<Plus />} onClick={() => navigate({ to: '/plans', search: { section: 'lifetime' } })}>
              {t('Choose plan')}
            </Btn>
          )}
        </div>

        <Group title={t('Manage')}>
          {!lifetimeSection && !sub.isTrial && recurrent && (
            <Row
              icon={<RefreshCw />}
              title={t('Auto-payment')}
              hint={recurrentHint}
              right={
                <Switch
                  checked
                  disabled={cancellingRecurrent}
                  aria-label={t('Auto-payment')}
                  onCheckedChange={() => void askCancelRecurrent()}
                />
              }
            />
          )}
          {!lifetimeSection && !sub.isTrial && !recurrent && (
            <>
              <Row
                icon={<RefreshCw />}
                title={t('Auto-renew')}
                hint={t('Auto-renew desc')}
                right={<Switch checked={autoRenew} aria-label={t('Auto-renew')} onCheckedChange={toggleAutoRenew} />}
              />
              {autoRenew && (
                <div className="flex flex-wrap items-center gap-2 px-4 py-3">
                  <span className="text-[13px] text-[var(--t-muted)]">{t('Auto renew period')}</span>
                  {[null, ...(autoRenewDays != null && ![7, 14, 30].includes(autoRenewDays) ? [7, 14, 30, autoRenewDays].sort((a, b) => a - b) : [7, 14, 30])].map((d) => (
                    <button
                      key={d ?? 'plan'}
                      type="button"
                      onClick={() => setAutoRenewDays(d)}
                      className={cn(
                        'rounded-[var(--r-pill)] px-3 py-1.5 text-[13px] font-medium transition-colors',
                        autoRenewDays === d ? 'bg-foreground text-background' : 'bg-[var(--t-fill)] text-[var(--t-muted)]',
                      )}
                    >
                      {d == null ? t('Auto renew period plan') : t('Auto renew period custom', { count: d })}
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
          {!lifetimeSection && (
            <Row icon={<ArrowUp />} title={t('More devices')} hint={t('More devices hint')} onClick={() => navigate({ to: '/upgrade' })} />
          )}
          {lifetimeSection && !sub.isTrial && (
            <>
              <Row
                icon={<PlusSquare />}
                title={t('Buy GB')}
                hint={sub.addonsAvailable ? undefined : t('Addons unavailable')}
                onClick={() => setAddonKind('traffic')}
              />
              <Row
                icon={<Smartphone />}
                title={t('Add devices')}
                hint={sub.addonsAvailable ? undefined : t('Addons unavailable')}
                onClick={() => setAddonKind('devices')}
              />
            </>
          )}
          {!lifetimeSection && !sub.isTrial && (
            <Row icon={<PlusSquare />} title={t('Buy GB')} hint={t('Buy GB hint')} onClick={() => setTrafficOpen(true)} />
          )}
          {!lifetimeSection && (
            <Row
              icon={<Snowflake />}
              title={isFrozen ? t('Unfreeze') : t('Pause subscription')}
              hint={isFrozen ? undefined : freezeBlocked ? t(TRIAL_FREEZE_NOTE_KEY) : t('Pause hint')}
              onClick={openFreeze}
            />
          )}
          <RotateLinkRow sub={sub} />
          <CountriesRow subscriptionId={sub.id} />
          <Row
            icon={<Globe2 />}
            title={t('Locations')}
            onClick={() => navigate({ to: '/locations', search: { section: lifetimeSection ? 'lifetime' : 'term' } })}
          />
        </Group>

        <Group title={lifetimeSection ? t('Devices') : t('Devices of', { used: devices?.length ?? 0, total: sub.devices })}>
          {lifetimeSection ? (
            <Row icon={<Smartphone />} title={t('Lifetime devices', { count: sub.devices })} onClick={() => navigate({ to: '/devices' })} />
          ) : (
            <>
              {(devices ?? []).slice(0, 3).map((dev) => (
                <Row
                  key={dev.id}
                  icon={dev.os === 'iOS' || dev.os === 'Android' ? <Smartphone /> : <Laptop />}
                  title={<span className="line-clamp-2 [overflow-wrap:anywhere]">{dev.name}</span>}
                  hint={dev.geo ? `${t('Connected')} · ${dev.geo}` : t('Connected')}
                />
              ))}
              {devices && devices.length === 0 && <Row title={t('No devices yet')} />}
              <Row title={t('Manage devices')} onClick={() => navigate({ to: '/devices' })} />
            </>
          )}
        </Group>

        <Group>
          <Row icon={<Plus />} title={t('Buy another plan')} hint={t('Buy another plan hint')} onClick={() => navigate({ to: '/plans' })} />
          {customSubButtons.map((b, i) => (
            <Row
              key={`${b.text}-${i}`}
              title={b.text}
              onClick={() => {
                haptic('light');
                if (b.path) navigate({ to: b.path as '/' });
                else if (b.url) openInvoiceUrl(b.url);
              }}
            />
          ))}
        </Group>
      </div>

      <ConfirmDialog
        open={freezeOpen}
        onClose={() => setFreezeOpen(false)}
        onConfirm={() => freeze()}
        title={isFrozen ? t('Unfreeze title') : t('Freeze title')}
        description={
          isFrozen
            ? t('Unfreeze desc')
            : t('Freeze desc')
        }
        confirmLabel={freezing ? '…' : isFrozen ? t('Unfreeze') : t('Freeze')}
        destructive={!isFrozen}
      />

      <TrafficSheet open={trafficOpen} onClose={() => setTrafficOpen(false)} subscriptionId={sub.id} />
      <LifetimeAddonSheet
        kind={addonKind}
        onClose={() => setAddonKind(null)}
        sub={sub}
      />
    </>
  );
}

/**
 * Вывод реферального заработка в карточке «Пригласи друга» (идея #162).
 *
 * Блок есть, только если партнёр включил вывод (или у клиента висит заявка, которую
 * можно отменить). Пока ответ едет, на месте сумм скелетон: «Можно вывести 0 ₽» до
 * загрузки читалось бы как факт. Упал запрос — блока нет: про вывод, который партнёр
 * мог и не включать, ошибку не показываем, 5xx и так ловит корневой тост.
 */
export function ReferralWithdrawBlock({ friends }: { friends?: number }) {
  const { t, i18n } = useTranslation();
  const { data, isPending } = useQuery({
    queryKey: WITHDRAW_QUERY_KEY,
    queryFn: () => api.referralWithdrawState(),
    staleTime: 30_000,
  });

  if (isPending) return <ReferralWithdrawBlockSkeleton cells={friends === undefined ? 2 : 3} />;
  if (!data || (!data.enabled && !data.open)) return null;

  const money = (minor: number) => formatWholeMinor(minor, data.currency, i18n.language);
  const canWithdraw = data.enabled && data.available_display_minor >= data.min_display_minor;
  // Владелец сервиса нажал «Перевожу»: заявка уже не «ждёт», и отменить её нельзя.
  const sending = data.open?.status === 'processing';
  const cells: { value: string; label: string; ok?: boolean }[] = [
    ...(friends === undefined ? [] : [{ value: String(friends), label: t('Invited word', { count: friends }) }]),
    // Короткие подписи: в три колонки «Заработано по рефералке» не помещалось.
    { value: money(data.earned_display_minor), label: t('Earned short') },
    { value: money(data.available_display_minor), label: t('To withdraw short'), ok: data.available_display_minor > 0 },
  ];

  return (
    <div className="overflow-hidden rounded-[var(--r-sm)] bg-[var(--t-surface-2)]">
      <div className="grid divide-x divide-[var(--t-line)]" style={{ gridTemplateColumns: `repeat(${cells.length}, minmax(0, 1fr))` }}>
        {cells.map((c) => (
          <div key={c.label} className="min-w-0 px-3.5 py-3">
            <div className={cn('truncate text-[18px] font-semibold tabular', c.ok && 'text-[var(--t-ok)]')}>{c.value}</div>
            <div className="mt-0.5 truncate text-[12px] text-[var(--t-muted)]">{c.label}</div>
          </div>
        ))}
      </div>
      <Link
        to="/referral-withdraw"
        className={cn(
          'flex min-h-[46px] items-center justify-between gap-2 border-t border-[var(--t-line)] px-3.5 py-2.5 text-[13px] font-medium transition-opacity active:opacity-70',
          data.open ? (sending ? 'text-foreground' : 'text-[var(--t-warn)]') : canWithdraw ? 'text-foreground' : 'text-[var(--t-muted)]',
        )}
      >
        <span className="flex min-w-0 items-center gap-2">
          {data.open ? (
            sending ? <Send className="size-4 shrink-0" /> : <Clock className="size-4 shrink-0" />
          ) : canWithdraw ? (
            <Wallet className="size-4 shrink-0" />
          ) : null}
          <span className="leading-snug">
            {data.open
              ? sending
                ? t('Withdraw processing short', { amount: money(data.open.amount_display_minor) })
                : t('Withdraw open short', { amount: money(data.open.amount_display_minor) })
              : canWithdraw
                ? t('Withdraw submit')
                : // Не хватает до минимума: кнопка «Вывести» вела бы в отказ. Экран вывода
                  // всё равно доступен, там объяснение и история заявок.
                  t('Withdraw from min', { amount: money(data.min_display_minor) })}
          </span>
        </span>
        <ChevronRight className="size-4 shrink-0 text-[var(--t-faint)]" />
      </Link>
    </div>
  );
}

/** Повторяет плашку сумм: колонки «число над подписью» и строка действия. */
function ReferralWithdrawBlockSkeleton({ cells }: { cells: number }) {
  return (
    <div className="overflow-hidden rounded-[var(--r-sm)] bg-[var(--t-surface-2)]">
      <div className="grid" style={{ gridTemplateColumns: `repeat(${cells}, minmax(0, 1fr))` }}>
        {Array.from({ length: cells }).map((_, i) => (
          <div key={i} className="space-y-1.5 px-3.5 py-3">
            <SkeletonLine size="lg" className="w-1/2" />
            <SkeletonLine size="xs" className="w-2/3" />
          </div>
        ))}
      </div>
      <div className="h-[46px] border-t border-[var(--t-line)]" />
    </div>
  );
}

/** Подтверждающий тап раньше этого срока считаем вторым щелчком двойного клика. */
const ROTATE_CONFIRM_DELAY_MS = 600;

/**
 * «Обновить ссылку» — отзыв публичного адреса подписки.
 *
 * Механика и текст те же, что у кнопки в боте. Разница была в доступности: в боте
 * она лежит прямо в меню подписки, а в мини-аппе была закопана внутри свёрнутого
 * блока на экране успешной покупки — до неё доходили три тапа и скролл. Здесь она
 * стоит в карточке устройств, где человек и задаётся вопросом «кто ещё подключён».
 *
 * Кулдаун проверяем ДО нажатия (бэк отдаёт `linkRotationAvailableAt`): раньше про
 * него узнавали только по ошибке, уже нажав.
 */
function RotateLinkRow({ sub }: { sub: Subscription }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [confirming, setConfirming] = useState(false);
  const [newUrl, setNewUrl] = useState<string | null>(null);
  const confirmTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Когда показали «Нажмите ещё раз». Второй щелчок двойного клика приходил через
  // доли секунды и сам становился подтверждением: ссылка менялась, хотя человек
  // предупреждение ещё не прочёл.
  const armedAt = useRef(0);
  const busyRef = useRef(false);

  useEffect(() => () => {
    if (confirmTimer.current) clearTimeout(confirmTimer.current);
  }, []);

  const availableAt = sub.linkRotationAvailableAt
    ? new Date(sub.linkRotationAvailableAt)
    : null;
  const waitMinutes =
    availableAt && availableAt.getTime() > Date.now()
      ? Math.ceil((availableAt.getTime() - Date.now()) / 60000)
      : 0;

  const rotate = useMutation({
    mutationFn: () => api.rotateSubscriptionLink(sub.id),
    onSettled: () => {
      busyRef.current = false;
    },
    onSuccess: (resp) => {
      setConfirming(false);
      if (!resp?.subscription_url) {
        haptic('error');
        toast.error(t('Rotate failed'));
        return;
      }
      haptic('success');
      setNewUrl(resp.subscription_url);
      void qc.invalidateQueries({ queryKey: ['subs'] });
    },
    onError: () => {
      setConfirming(false);
      haptic('error');
      toast.error(t('Rotate failed'));
    },
  });

  const onClick = () => {
    if (waitMinutes > 0 || rotate.isPending || busyRef.current) return;
    if (!confirming) {
      // Двухшаговое подтверждение вместо попапа: действие необратимое, но не
      // разрушительное, а лишний системный диалог на этом экране только мешает.
      setConfirming(true);
      armedAt.current = Date.now();
      confirmTimer.current = setTimeout(() => setConfirming(false), 5000);
      return;
    }
    if (Date.now() - armedAt.current < ROTATE_CONFIRM_DELAY_MS) return;
    if (confirmTimer.current) clearTimeout(confirmTimer.current);
    busyRef.current = true;
    rotate.mutate();
  };

  // Геометрия как у Row: строка стоит в одной группе с ними.
  return (
    <div>
      <button
        type="button"
        onClick={onClick}
        disabled={waitMinutes > 0 || rotate.isPending}
        className="tl-row flex min-h-[62px] w-full items-center gap-3.5 px-4 py-3 text-left active:bg-[var(--t-fill)] disabled:opacity-45"
      >
        <span className="tl-row-ic grid size-9 shrink-0 place-items-center rounded-[var(--r-sm)] bg-[var(--t-surface-2)] text-foreground">
          <Link2 className="size-[18px]" />
        </span>
        <span className="min-w-0 flex-1">
          <span className={cn('block text-[15px] font-medium leading-snug', confirming && 'text-[var(--t-warn)]')}>
            {rotate.isPending ? t('Rotating') : confirming ? t('Rotate confirm') : t('Rotate link')}
          </span>
          <span className="mt-0.5 block text-[13px] leading-snug text-[var(--t-muted)]">
            {waitMinutes > 0 ? t('Rotate wait', { count: waitMinutes }) : t('Rotate warn short')}
          </span>
        </span>
      </button>
      {!newUrl && confirming && <p className="px-4 pb-3 text-[12px] leading-snug text-[var(--t-muted)]">{t('Rotate note')}</p>}
      {newUrl && (
        <div className="mx-4 mb-3 space-y-1 rounded-[var(--r-sm)] bg-[var(--t-fill)] p-3">
          <div className="text-xs text-[var(--t-muted)]">{t('New sub address')}</div>
          <div className="break-all font-mono text-[11px] text-foreground">{newUrl}</div>
        </div>
      )}
    </div>
  );
}

/**
 * Вход в выбор стран подписки (заявка #169). Нет, пока не знаем, разрешил ли его
 * владелец сервиса: строка, ведущая в запертый экран, только путала бы.
 */
function CountriesRow({ subscriptionId }: { subscriptionId: string }) {
  const { t } = useTranslation();
  const { data } = useQuery({
    queryKey: ['sub-countries', subscriptionId],
    queryFn: () => api.subscriptionCountries(subscriptionId),
    staleTime: 60 * 1000,
  });
  if (!data?.enabled || data.countries.length < 2) return null;
  const total = data.countries.length;
  const shown = total - data.hidden.length;
  return (
    <Link
      to="/countries/$subscriptionId"
      params={{ subscriptionId }}
      preload="intent"
      className="tl-row flex min-h-[62px] w-full items-center gap-3.5 px-4 py-3 active:bg-[var(--t-fill)]"
    >
      <span className="tl-row-ic grid size-9 shrink-0 place-items-center rounded-[var(--r-sm)] bg-[var(--t-surface-2)] text-foreground">
        <ListChecks className="size-[18px]" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-[15px] font-medium leading-snug">{t('Countries in subscription')}</span>
        <span className="mt-0.5 block text-[13px] text-[var(--t-muted)] tabular">
          {data.hidden.length ? t('Countries shown of', { shown, total }) : t('All countries shown')}
        </span>
      </span>
      <ChevronRight className="size-[18px] shrink-0 text-[var(--t-faint)]" />
    </Link>
  );
}



/**
 * Повторяет карточку тарифа поблочно: заголовок со статусом, две полосы
 * (дни и трафик), строка автопродления. Глухая плита на 260px, которая тут
 * стояла раньше, ничего не обещала — экран всё равно перекладывался целиком.
 */
/** Та же геометрия, что у экрана: заголовок, карта, трафик, ссылка, две кнопки, группа строк. */
export function DashboardSkeleton() {
  const { t } = useTranslation();
  return (
    <SkeletonPage className="space-y-4 p-4">
      <ScreenTitle>{t('Subscription tab')}</ScreenTitle>
      <Skeleton className="h-[148px] w-full rounded-[var(--r-subcard)]" />
      <SkeletonCard className="min-h-0 space-y-3.5 rounded-[var(--r-card)] p-4">
        <SkeletonLine size="sm" className="w-32" />
        <SkeletonLine size="hero" className="w-1/3" />
        <Skeleton className="h-[22px] w-full rounded-[3px]" />
        <SkeletonLine size="xs" className="w-2/3" />
      </SkeletonCard>
      <SkeletonCard className="h-[58px] min-h-0 rounded-[var(--r-card)]" />
      <div className="grid grid-cols-2 gap-2">
        <Skeleton className="h-[52px] rounded-[var(--r-btn)]" />
        <Skeleton className="h-[52px] rounded-[var(--r-btn)]" />
      </div>
      <div>
        <h2 className="mb-2 px-1 text-[13px] font-medium text-[var(--t-muted)]">{t('Manage')}</h2>
        <SkeletonCard className="min-h-0 divide-y divide-[var(--t-line)] rounded-[var(--r-card)]">
          {Array.from({ length: 4 }, (_, i) => (
            <div key={i} className="flex min-h-[62px] items-center gap-3.5 px-4">
              <Skeleton className="size-9 shrink-0 rounded-[var(--r-sm)]" />
              <SkeletonLine className="w-1/3" />
              <Skeleton className="ml-auto size-[22px] shrink-0 rounded-full" />
            </div>
          ))}
        </SkeletonCard>
      </div>
    </SkeletonPage>
  );
}




/**
 * Подписка закончилась, но её можно продлить с той же ссылкой: настраивать
 * устройства заново не нужно. Если тариф сняли с продажи или это спецтариф, продлить
 * нельзя, и кнопка ведёт в выбор тарифа.
 */
export function ExpiredCard({ sub }: { sub: Subscription & { renewable: boolean } }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  // Та же карта, что у живой подписки, только погасшая: человек узнаёт свою подписку.
  return (
    <div className="space-y-3">
      <SubCard
        mini
        frozen
        tone="warn"
        status={t('Expired title')}
        title={sub.planName}
        meta={t('Expired on', { date: shortDate(sub.expiresAt, i18n.language) })}
      />
      <p className="px-1 text-[14px] leading-snug text-[var(--t-muted)]">
        {sub.renewable ? t('Expired renew hint') : t('Expired gone hint')}
      </p>
      <Btn
        className="w-full"
        icon={<RefreshCw />}
        onClick={() =>
          sub.renewable
            ? navigate({ to: '/renew/$subscriptionId', params: { subscriptionId: sub.id } })
            : navigate({ to: '/plans' })
        }
      >
        {sub.renewable ? t('Renew') : t('Go to paid plan')}
      </Btn>
    </div>
  );
}

function TrafficSheet({ open, onClose, subscriptionId }: { open: boolean; onClose: () => void; subscriptionId: string | null }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [selected, setSelected] = useState<number | null>(null);
  // Пусто до ответа, дальше первый способ сервера (СБП), а не CryptoBot.
  const [provider, setProvider] = useState<string>('');

  const { data: packs } = useQuery({
    queryKey: ['traffic-packs', subscriptionId],
    queryFn: () => (subscriptionId ? api.trafficPacks(subscriptionId) : Promise.resolve({ packs: [] })),
    enabled: !!subscriptionId,
  });
  const { data: providersData } = useQuery({
    queryKey: ['payment-providers'],
    queryFn: () => api.paymentProviders(),
  });

  useEffect(() => {
    if (providersData?.providers && providersData.providers.length > 0 && !providersData.providers.includes(provider)) {
      setProvider(providersData.providers[0]);
    }
  }, [providersData, provider]);

  const { mutate: buyPack, isPending } = useMutation({
    mutationFn: (gb: number) => {
      if (!subscriptionId) throw new Error('no subscription');
      return api.createTrafficIntent(subscriptionId, { gb_amount: gb, provider });
    },
    onSuccess: (intent) => {
      openInvoiceUrl(intent.invoice_url);
      haptic('success');
      toast.success(t('Traffic invoice open'));
      onClose();
      navigate({ to: '/processing/$paymentId', params: { paymentId: intent.payment_id } });
    },
    onError: (e: Error) => {
      toast.error(t('Invoice failed detail', { error: e.message }));
    },
  });

  const selectedPack = packs?.packs.find((p) => p.gb === selected);
  const priceLabel = (cost: number, currency: string) =>
    currency === 'RUB' ? rub(Math.floor(cost / 100)) : `${groupDigits(Math.floor(cost / 100))} ${currency}`;

  return (
    <BottomSheet open={open} onClose={onClose} title={t('Buy traffic')}>
      <p className="text-sm text-[var(--t-muted)]">
        {t('Buy traffic desc')}
      </p>
      <div className="space-y-2">
        {(packs?.packs ?? []).map((p) => (
          <button
            key={p.gb}
            type="button"
            onClick={() => setSelected(p.gb)}
            className={cn(
              'w-full text-left p-4 rounded-2xl border transition-transform duration-100 flex items-center justify-between active:scale-[0.98]',
              selected === p.gb ? 'border-foreground bg-[var(--t-surface)]' : 'calm-card',
            )}
          >
            <div className="font-bold text-foreground">
              {p.gb >= 9000 ? t('GB pack unlimited') : t('GB pack', { gb: p.gb })}
            </div>
            <div className="font-bold tabular text-foreground">
              {priceLabel(p.total_display || p.cost_display, p.currency)}
            </div>
          </button>
        ))}
      </div>
      <ProviderPills providers={providersData?.providers ?? []} value={provider} onChange={setProvider} />
      <button
        type="button"
        disabled={!selected || !provider || isPending}
        onClick={() => selected && provider && buyPack(selected)}
        className="w-full calm-cta py-3 rounded-[var(--r-btn)] text-sm font-bold flex items-center justify-center gap-2 disabled:opacity-50 active:scale-[0.98] transition-transform"
      >
        {isPending && <Loader2 className="w-4 h-4 animate-spin" />}
        {selectedPack
          ? t('Buy for', {
              price: priceLabel(
                selectedPack.total_display || selectedPack.cost_display,
                selectedPack.currency,
              ),
            })
          : t('Pick a pack')}
      </button>
    </BottomSheet>
  );
}

/**
 * Докупка к «Навсегда»: пакет трафика обхода или +15 устройств.
 *
 * Два способа, как у тарифа: счётом (выбранный способ оплаты) и с баланса, если его
 * хватает. С баланса докупка применяется сразу, счётом после оплаты (экран
 * `processing`, как у остальных счетов).
 */
function LifetimeAddonSheet({
  kind,
  onClose,
  sub,
}: {
  kind: 'traffic' | 'devices' | null;
  onClose: () => void;
  sub: Subscription;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [provider, setProvider] = useState<string>('');
  // Ключ намерения на одно открытие шторки: двойной тап и повтор после сбоя
  // поставщика не покупают второй раз. После успеха шторка закрывается, а следующее
  // открытие берёт новый ключ.
  const [nonce, setNonce] = useState(newIntentNonce);
  const open = kind !== null;

  const { data, isPending, isError, refetch } = useQuery({
    queryKey: ['lifetime-addons', sub.id],
    queryFn: () => api.lifetimeAddons(sub.id),
    enabled: open && Boolean(sub.addonsAvailable),
  });
  const { data: providersData } = useQuery({
    queryKey: ['payment-providers'],
    queryFn: () => api.paymentProviders(),
    enabled: open,
  });

  useEffect(() => {
    if (open) setNonce(newIntentNonce());
    else setSelectedKey(null);
  }, [open]);
  useEffect(() => {
    const list = providersData?.providers ?? [];
    if (list.length > 0 && !list.includes(provider)) setProvider(list[0]);
  }, [providersData, provider]);

  const offers = (data?.offers ?? []).filter((o) => o.kind === kind);
  useEffect(() => {
    // Устройства продаются одним пакетом: выбирать там нечего.
    if (kind === 'devices' && offers.length === 1 && selectedKey === null) {
      setSelectedKey(offers[0].key);
    }
  }, [kind, offers, selectedKey]);
  const selected = offers.find((o) => o.key === selectedKey) ?? null;
  const balanceEnough = selected !== null && (data?.balance_usd_minor ?? 0) >= selected.cost_usd_minor;
  const rubMinor = (minor: number) => rub(Math.floor(minor / 100));

  const invoice = useMutation({
    mutationFn: () => {
      if (!selected || !kind) throw new Error('no add-on');
      return api.createAddonIntent(sub.id, { kind, key: selected.key, provider, nonce });
    },
    onSuccess: (intent) => {
      openInvoiceUrl(intent.invoice_url);
      haptic('success');
      onClose();
      navigate({ to: '/processing/$paymentId', params: { paymentId: intent.payment_id } });
    },
    onError: (e: Error) => {
      haptic('error');
      toast.error(t('Invoice failed detail', { error: e.message }));
    },
  });
  const fromBalance = useMutation({
    mutationFn: () => {
      if (!selected || !kind) throw new Error('no add-on');
      return api.buyAddonFromBalance(sub.id, { kind, key: selected.key, nonce });
    },
    onSuccess: (res) => {
      haptic('success');
      toast.success(
        kind === 'devices'
          ? t('Addon done devices', { count: res.devices })
          : t('Addon done traffic', { gb: selected?.gb ?? 0 }),
      );
      qc.invalidateQueries({ queryKey: ['subs'] });
      qc.invalidateQueries({ queryKey: ['profile'] });
      qc.invalidateQueries({ queryKey: ['lifetime-addons', sub.id] });
      onClose();
    },
    onError: (e: Error) => {
      haptic('error');
      const code = e instanceof ApiError ? e.code : undefined;
      toast.error(
        code === 'app.subscriptions.insufficient_balance'
          ? t('Addon no balance')
          : isProviderError(e)
            ? t('Provider unavailable')
            : e.message,
      );
    },
  });
  const busy = invoice.isPending || fromBalance.isPending;

  return (
    <BottomSheet
      open={open}
      onClose={onClose}
      title={kind === 'devices' ? t('Add devices title') : t('Buy traffic')}
    >
      <p className="text-sm text-[var(--t-muted)]">
        {kind === 'devices'
          ? t('Add devices desc', { now: sub.devices, after: sub.devices + 15 })
          : t('Lifetime traffic desc')}
      </p>
      {!sub.addonsAvailable ? (
        <div className="rounded-[var(--r-card)] calm-card p-4 text-sm text-[var(--t-muted)]">{t('Addons unavailable')}</div>
      ) : isPending ? (
        <div className="space-y-2">
          {[0, 1].map((i) => (
            <Skeleton key={i} className="h-[60px] rounded-2xl" />
          ))}
        </div>
      ) : isError ? (
        <div className="rounded-[var(--r-card)] calm-card p-4 text-sm text-[var(--t-muted)] space-y-2">
          <div>{t('Addons load failed')}</div>
          <button type="button" onClick={() => refetch()} className="font-semibold text-foreground min-h-[44px]">
            {t('Retry')}
          </button>
        </div>
      ) : offers.length === 0 ? (
        <div className="rounded-[var(--r-card)] calm-card p-4 text-sm text-[var(--t-muted)]">{t('Addons unavailable')}</div>
      ) : (
        <>
          <div className="space-y-2">
            {offers.map((o) => (
              <button
                key={o.key}
                type="button"
                onClick={() => setSelectedKey(o.key)}
                className={cn(
                  'w-full text-left p-4 rounded-2xl border transition-transform duration-100 flex items-center justify-between active:scale-[0.98]',
                  selectedKey === o.key ? 'border-foreground bg-[var(--t-surface)]' : 'calm-card',
                )}
              >
                <div className="font-bold text-foreground">
                  {o.kind === 'devices'
                    ? t('Addon devices pack', { count: o.devices ?? 15 })
                    : t('GB pack', { gb: o.gb ?? 0 })}
                </div>
                <div className="font-bold tabular text-foreground">{rubMinor(o.total_display || o.cost_display)}</div>
              </button>
            ))}
          </div>
          <ProviderPills providers={providersData?.providers ?? []} value={provider} onChange={setProvider} />
          <button
            type="button"
            disabled={!selected || !provider || busy}
            onClick={() => invoice.mutate()}
            className="w-full calm-cta py-3 rounded-[var(--r-btn)] text-sm font-bold flex items-center justify-center gap-2 disabled:opacity-50 active:scale-[0.98] transition-transform"
          >
            {invoice.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
            {selected
              ? t('Buy for', { price: rubMinor(selected.total_display || selected.cost_display) })
              : t('Pick a pack')}
          </button>
          {selected && balanceEnough && (
            <button
              type="button"
              disabled={busy}
              onClick={() => fromBalance.mutate()}
              className="w-full rounded-xl bg-[var(--t-fill)] py-3 text-sm font-bold text-foreground flex items-center justify-center gap-2 disabled:opacity-50 active:scale-[0.98] transition-transform"
            >
              {fromBalance.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
              {t('Pay from balance price', { price: rubMinor(selected.cost_display) })}
            </button>
          )}
        </>
      )}
    </BottomSheet>
  );
}
